import React, { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'

import { getXCLEARCorrelationInfo } from '@api/utils'
import { Enrollment as EnrollmentClient } from '@clear-denver/tpc-openapi-typescript'
import AuthFlow from '@components/pages/auth/AuthFlow'
import { useEnrollmentContext } from '@contexts/EnrollmentContext'
import { yupResolver } from '@hookform/resolvers/yup'
import useAuthenticationApi from '@hooks/useAuthenticationApi'
import { useAuthPipe } from '@hooks/useAuthPipe'
import { useFlag } from '@hooks/useFlag'
import { DiscreteEvent } from '@utils/customEvents'
import { trackFunnelStep } from '@utils/trackFunnelStep'
import { authSchema } from '@validators/schema/enroll/authSchema'

export interface FormData extends Partial<EnrollmentClient.BiographicsLookup> {
  otpSessionId: string
}

export type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

export enum FormSteps {
  CONTACT = 'Contact',
  VERIFICATION = 'Verification',
}

const AuthPage: React.FC = () => {
  const router = useRouter()
  const { client: authenticationClient } = useAuthenticationApi()
  const { setBiographicAuthInfo, setApptInfo } = useEnrollmentContext()
  const pipe = useAuthPipe()
  const attemptsRef = useRef(0)
  const displayAuthenticationRevisedLayout =
    useFlag('layoutRefresh')?.displayAuthenticationRevisedLayout

  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  const { register, handleSubmit, formState } = useForm<any>({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    setApptInfo(null)
  }, [setApptInfo])

  const [step, setStep] = useState<FormSteps>(FormSteps.CONTACT)
  const { errors, isValid } = formState
  const [otpFailureMessage, setOtpFailureMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const desiredApply = router.query?.desiredPath === '/enroll/you'
  const desiredRenew =
    router.query?.desiredPath === '/renewal/eligibility/eligibility'

  useEffect(() => {
    const isRedirectEntry = !!router.query?.desiredPath
    pipe.view({
      event: DiscreteEvent.AUTH_VIEW,
      startSession: !isRedirectEntry,
    })
  }, [pipe, router.query?.desiredPath])

  useEffect(() => {
    if (desiredApply) {
      trackFunnelStep('apply', 2, 'auth')
    }
    if (desiredRenew) {
      trackFunnelStep('renew', 2, 'auth')
    }
  }, [desiredApply, desiredRenew])

  async function onSubmitContact(formData: InitiateAuth) {
    setIsLoading(true)
    const { phoneNumber, email } = formData
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const formatedPhoneNumber = phoneNumber ? `+1.${phoneNumber}` : null

    pipe.initiate({
      status: 'begin',
      xCLEARCorrelationInfo,
      url: router.route,
    })

    try {
      const response = await authenticationClient.initiateAuth({
        xCLEARCorrelationInfo,
        otpForm: {
          phoneNumber: formatedPhoneNumber,
          emailAddress: email,
        },
      })
      setBiographicAuthInfo({
        phoneNumber: formatedPhoneNumber,
        emailAddress: email,
      })

      const hasOtpSessionId = !!response?.otpSessionId

      if (hasOtpSessionId) {
        setVerificationFormData({
          ...formData,
          otpSessionId: response.otpSessionId,
        })
        setStep(FormSteps.VERIFICATION)
        attemptsRef.current = 0
      }

      pipe.initiate({ status: 'success', hasOtpSessionId })

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      if (error?.status === 401) {
        pipe.fail({
          type: 'SESSION_EXPIRED',
          reason: '401 during auth initiation',
          meta: { status: '401' },
        })
      }
      pipe.initiate({ status: 'failure', error })
    }
  }

  async function onVerificationSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    attemptsRef.current += 1

    pipe.verify({
      status: 'begin',
      xCLEARCorrelationInfo,
      url: router.route,
    })

    try {
      await authenticationClient.otpChallenge({
        xCLEARCorrelationInfo,
        otpCode: {
          otpSessionId: verificationFormData.otpSessionId,
          otpCode: formData.verificationCode,
        },
      })

      const hasDesiredPath = !!router.query?.desiredPath
      pipe.verify({ status: 'success', hasDesiredPath })

      if (hasDesiredPath) {
        const desiredPath = router.query?.desiredPath as string | undefined
        if (!verificationFormData.otpSessionId) {
          pipe.fail({
            type: 'MISSING_OTP_SESSION',
            reason: 'Navigating to desiredPath without otpSessionId',
            meta: { desiredPath },
          })
        }
        router.push(
          {
            pathname: desiredPath,
            query: {
              otpSessionId: verificationFormData?.otpSessionId,
            },
          },
          desiredPath
        )
      } else {
        pipe.fail({
          type: 'NO_DESIRED_PATH',
          reason: 'Verification succeeded but no desiredPath to navigate to',
        })
        router.push('/')
      }

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setOtpFailureMessage(
        'Invalid verification code provided, please try again.'
      )

      pipe.verify({
        status: 'failure',
        error,
        attempts: attemptsRef.current,
      })
    }
  }

  useEffect(() => {
    document.documentElement.style.backgroundColor = '#FBFBF9'
    return () => {
      document.documentElement.style.backgroundColor = ''
    }
  }, [])

  return (
    <AuthFlow
      displayAuthenticationRevisedLayout={displayAuthenticationRevisedLayout}
      step={step}
      verificationFormData={verificationFormData}
      onVerificationSubmit={onVerificationSubmit}
      otpFailureMessage={otpFailureMessage}
      setOtpFailureMessage={setOtpFailureMessage}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
      onSubmitContact={onSubmitContact}
      register={register}
      errors={errors}
      isValid={isValid}
    />
  )
}

export default AuthPage
